import React, { useCallback, useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSubmitInternalClaim } from '~/hooks/apis/claimCampaigns';

import type { ClaimCampaign, InternalClaimActionResult, InternalSubmitClaimResponse } from '~/types/claimCampaignTypes';

import { getActionStatusColor } from './claimCampaignUtils';

type CampaignProp = Pick<ClaimCampaign, 'id' | 'name' | 'validatorType'>;

interface SubmitClaimDialogProps {
  open: boolean;
  campaign: CampaignProp | null;
  onClose: () => void;
  /** When provided, the code field is pre-filled and read-only. */
  prefillCode?: string;
}

interface FormData {
  customerId: string;
  code: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function SubmitClaimDialog({ open, campaign, onClose, prefillCode }: SubmitClaimDialogProps) {
  const submitMutation = useSubmitInternalClaim();
  const [result, setResult] = useState<InternalSubmitClaimResponse['result'] | null>(null);

  const requiresCode = campaign?.validatorType === 'PREGEN_CODE' || campaign?.validatorType === 'SHARED_CODE';

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { customerId: '', code: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (open) {
      reset({ customerId: '', code: prefillCode ?? '' });
      setResult(null);
    }
  }, [open, reset, prefillCode]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!campaign) return;

      try {
        const response = await submitMutation.mutateAsync({
          campaignId: campaign.id,
          body: {
            customerId: data.customerId.trim(),
            code: requiresCode ? data.code.trim() : undefined,
          },
        });
        setResult(response.result);
      } catch {
        // Error handled in mutation hook
      }
    },
    [campaign, submitMutation, requiresCode],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Claim — {campaign?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!result && (
            <>
              <Typography variant="body2" color="text.secondary">
                Submit a claim on behalf of a customer. This will execute the campaign actions for the specified
                customer.
              </Typography>

              <Controller
                name="customerId"
                control={control}
                rules={{
                  required: 'Customer ID is required',
                  pattern: { value: UUID_PATTERN, message: 'Must be a valid UUID' },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Customer ID"
                    required
                    fullWidth
                    placeholder="e.g. 12345678-1234-1234-1234-123456789abc"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />

              {requiresCode && (
                <Controller
                  name="code"
                  control={control}
                  rules={{ required: 'Code is required for this campaign type' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Code"
                      required
                      fullWidth
                      disabled={!!prefillCode}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            </>
          )}

          {result && (
            <Stack spacing={2}>
              <Alert severity={result.claimed ? 'success' : 'error'}>
                {result.claimed ? 'Claim processed successfully' : 'Claim processing failed'}
              </Alert>

              <Box>
                <Typography variant="body2">
                  <strong>Claim Record ID:</strong> {result.claimRecordId}
                </Typography>
              </Box>

              {result.actions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Actions
                  </Typography>
                  <Stack spacing={1}>
                    {result.actions.map((action: InternalClaimActionResult, index: number) => (
                      <Box key={index} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                          <Chip label={action.type} size="small" variant="outlined" />
                          <Chip label={action.status} size="small" color={getActionStatusColor(action.status)} />
                        </Stack>
                        {action.errorMessage && (
                          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                            {action.errorMessage}
                          </Typography>
                        )}
                        {action.result && (
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', mt: 0.5 }}
                          >
                            {JSON.stringify(action.result, null, 2)}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={submitMutation.isPending}>
            {submitMutation.isPending ? 'Submitting...' : 'Submit Claim'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
